import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { emptyArray } from 'ui-components/default-props/empty';
import { Hotkey } from 'ui-components/components/hotkey';
import { OperatingSystemService } from 'ui-components/services/operating-system-service';

import { hotkeyApplyPreset } from '../../store/seller/presets/seller.presets.actions';
import Global from '../../store/global-selectors';

const ctrlOrCmd = OperatingSystemService.isMac() ? 'Cmd' : 'Ctrl';

export const SellerPresetHotkeys = ({ children, fulfillmentPlanIds, onApplyPreset }) => {
  const dispatch = useDispatch();
  const presets = useSelector(Global.selectSellerPresets) || emptyArray;
  const applyPreset = useCallback(
    (hotkey) => {
      dispatch(
        hotkeyApplyPreset({
          hotkey,
          fulfillmentPlanIds,
          callbackWithPayload: onApplyPreset,
        }),
      );
    },
    [dispatch, fulfillmentPlanIds, onApplyPreset],
  );

  const presetHotkeys = useMemo(
    () =>
      presets.reduce((acc, preset) => {
        const { hotKey, name } = preset;

        if (hotKey) {
          const letterUppercase = hotKey.toUpperCase();
          const charCode = hotKey.charCodeAt(0);

          acc.push({
            displayHotkeyStrings: [ctrlOrCmd, letterUppercase],
            barcode: `+${charCode}=`,
            hotkey: [
              {
                key: hotKey,
                hasCtrlEquivalent: true,
                hasModifier: true,
              },
            ],
            handler: () => {
              applyPreset(hotKey);
            },
            name: `${ctrlOrCmd} + ${letterUppercase}`,
            doNotTrack: true,
            isCustomPreset: true,
            presetName: name,
          });
        }

        return acc;
      }, []),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [fulfillmentPlanIds, applyPreset, presets],
  );

  if (presetHotkeys.length === 0) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return presetHotkeys.reduce((acc, hotkey) => <Hotkey hotkey={hotkey}>{acc}</Hotkey>, children);
};

SellerPresetHotkeys.propTypes = {
  children: PropTypes.node,
  fulfillmentPlanIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onApplyPreset: PropTypes.func,
};

export default SellerPresetHotkeys;
